/* Guard de navegación SPA compatible con `<BrowserRouter>`.
 *
 * `useBlocker` y `unstable_usePrompt` de react-router solo existen con un data
 * router (`createBrowserRouter`); la app monta `<BrowserRouter>`, donde no están
 * soportados. Esto cubre el mismo hueco por debajo, en dos piezas:
 *
 *   1. `installNavigationGuard` (lo monta <NavigationGuard/> dentro del Router)
 *      envuelve el `navigator` —la `history— que el Router publica por contexto:
 *      push / replace / go cubren `<Link>`, `useNavigate` y `navigate(-1)`. Solo
 *      pregunta si CAMBIA la ruta (pathname): los `replace` internos que consumen
 *      una intención one-shot (`?reuseJobId`) no son una salida.
 *
 *   2. `installPopstateGuard` (lo instala index.js ANTES de montar React) cubre
 *      los botones atrás/adelante del navegador. No se puede impedir —la URL ya
 *      cambió—, así que se intercepta `popstate` y, si el usuario se queda, se
 *      deshace con `history.go(-delta)` usando el `idx` que react-router guarda en
 *      `history.state`. Tiene que registrarse ANTES que el listener del Router:
 *      React vacía el trabajo síncrono (y desmonta el editor) en el microtask que
 *      sigue al listener del Router, así que uno registrado después ya llega con el
 *      editor desmontado y el registro de trabajo sin enviar vacío.
 *
 * Lógica sin React para poder probarla con un navegador real sin montar la app.
 */

function pathnameOf(win, to) {
  try {
    if (to == null) return win.location.pathname;
    if (typeof to === "string") return new URL(to, win.location.href).pathname;
    if (typeof to === "object") return to.pathname == null ? win.location.pathname : new URL(to.pathname, win.location.href).pathname;
  } catch (_) { /* ruta ilegible: se trata como cambio */ }
  return null;
}

const idxOf = (win) => {
  const s = win.history && win.history.state;
  return s && Number.isInteger(s.idx) ? s.idx : null;
};

// ───────────────────────── atrás / adelante (popstate) ──────────────────────
let pop = null;   // singleton por ventana

/**
 * @param {Window} win
 * @param {() => boolean} confirm  true = se puede salir
 * @returns {() => void} desinstalador (idempotente: una 2ª llamada no duplica)
 */
export function installPopstateGuard(win, confirm) {
  if (pop && pop.win === win) { pop.confirm = confirm; return pop.dispose; }
  const st = {
    win, confirm, lastPath: win.location.pathname, lastIdx: idxOf(win), bypass: false, swallow: 0,
  };
  const sync = () => { st.lastPath = win.location.pathname; st.lastIdx = idxOf(win); };
  // Seguimiento de las navegaciones normales (no bloquea nada).
  const hist = win.history;
  const origPush = hist.pushState; const origReplace = hist.replaceState;
  hist.pushState = function trackedPush(...a) { const r = origPush.apply(this, a); sync(); return r; };
  hist.replaceState = function trackedReplace(...a) { const r = origReplace.apply(this, a); sync(); return r; };

  const onPop = (e) => {
    if (st.swallow > 0) { st.swallow -= 1; e.stopImmediatePropagation(); return; }
    if (st.bypass) { st.bypass = false; sync(); return; }
    if (win.location.pathname === st.lastPath) { sync(); return; }
    if (st.confirm()) { sync(); return; }
    // El usuario se queda: la URL se devuelve a donde estaba y el router no ve
    // nada de esto. Con `idx` se deshace exacto; sin él, se re-apila la URL.
    e.stopImmediatePropagation();
    const nowIdx = idxOf(win);
    if (st.lastIdx != null && nowIdx != null && st.lastIdx !== nowIdx) {
      st.swallow += 1;
      win.history.go(st.lastIdx - nowIdx);
    } else {
      origPush.call(win.history, win.history.state, "", st.lastPath + win.location.search + win.location.hash);
    }
  };
  win.addEventListener("popstate", onPop, true);
  st.dispose = () => {
    win.removeEventListener("popstate", onPop, true);
    hist.pushState = origPush; hist.replaceState = origReplace;
    if (pop === st) pop = null;
  };
  pop = st;
  return st.dispose;
}

/** Un `go()` ya confirmado: su popstate no vuelve a preguntar. */
export function allowNextPop(win) {
  if (!pop || pop.win !== win) return;
  pop.bypass = true;
  // El plazo evita que un `go()` fuera de rango (sin popstate) deje el permiso
  // armado para el siguiente retroceso del usuario.
  win.setTimeout(() => { if (pop) pop.bypass = false; }, 1500);
}

// ───────────────────────── enlaces / navigate() ─────────────────────────────
/**
 * @param {object} p
 * @param {object} p.navigator  `history` del Router (push/replace/go mutables)
 * @param {Window} p.win
 * @param {() => boolean} p.confirm  true = se puede salir
 * @returns {() => void} desinstalador
 */
export function installNavigationGuard({ navigator, win, confirm }) {
  const original = { push: navigator.push, replace: navigator.replace, go: navigator.go };
  navigator.push = function guardedPush(to, ...rest) {
    if (pathnameOf(win, to) !== win.location.pathname && !confirm()) return undefined;
    return original.push.call(navigator, to, ...rest);
  };
  navigator.replace = function guardedReplace(to, ...rest) {
    if (pathnameOf(win, to) !== win.location.pathname && !confirm()) return undefined;
    return original.replace.call(navigator, to, ...rest);
  };
  navigator.go = function guardedGo(delta) {
    if (delta !== 0 && !confirm()) return undefined;
    allowNextPop(win);
    return original.go.call(navigator, delta);
  };
  return () => {
    navigator.push = original.push;
    navigator.replace = original.replace;
    navigator.go = original.go;
  };
}
